import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {DeliveryModeConst, isValidDeliveryMode} from '../../core/constants/enums';

const deliveryModeColorKeys = {
    [DeliveryModeConst.PickupAndDelivery]: 'pickupAndDelivery',
    [DeliveryModeConst.PickupOnly]: 'pickupOnly',
    [DeliveryModeConst.DeliveryOnly]: 'deliveryOnly',
};

const useDeliveryMode = () => {
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.delivery-mode'});

    const options = useMemo(() => [
        {
            id: DeliveryModeConst.PickupAndDelivery,
            value: DeliveryModeConst.PickupAndDelivery,
            label: t('pickupAndDelivery'),
        },
        {
            id: DeliveryModeConst.PickupOnly,
            value: DeliveryModeConst.PickupOnly,
            label: t('pickupOnly'),
        },
        {
            id: DeliveryModeConst.DeliveryOnly,
            value: DeliveryModeConst.DeliveryOnly,
            label: t('deliveryOnly'),
        },
    ], [t]);

    /**
     * Returns the label for a given delivery mode.
     * @type {(function(*): (null|*))|*}
     */
    const getLabel = useCallback((deliveryMode) => {
        if (!isValidDeliveryMode(deliveryMode))
            return null;
        return options.find(option => option.value === deliveryMode)?.label ?? null;
    }, [options]);

    /**
     * Returns the color key for a given delivery mode, which can be used to determine styling.
     * @type {(function(*): (null|*))|*}
     */
    const getColorKey = useCallback((deliveryMode) => {
        if (!isValidDeliveryMode(deliveryMode))
            return null;
        return deliveryModeColorKeys[deliveryMode];
    }, []);

    return {options, getLabel, getColorKey, isValidDeliveryMode};
};

export default useDeliveryMode;
